import { useEffect, useRef, useState } from "react";
import { XStack, Text } from "tamagui";
import { RiTimeLine } from "react-icons/ri";

import { colors } from "@styles/colors";

/** 倒數到 0 之後，後端可能還沒過期（手機時鐘比伺服器快）：每隔這麼久再查一次，直到頁面切走。 */
export const LOCK_EXPIRED_RECHECK_MS = 10_000;

/** 剩餘毫秒 → m:ss（不足 0 當 0） */
export const formatLockRemaining = (ms: number) => {
  const totalSec = Math.max(0, Math.ceil(ms / 1000));
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
};

/**
 * 請款鎖倒數（2026-10-28 起：客戶打開付款頁開始鎖 5 分鐘，到期沒付就取消請款、連結失效）。
 * active 且有 expiresAt 時每秒更新，回傳剩餘毫秒（到期為 0）；沒鎖或不在作用中回 null。
 * 到期當下呼叫一次 onExpire（呼叫端重新查狀態，讓頁面切到「連結已失效」），
 * 之後若還停在可付款（時鐘差），每 LOCK_EXPIRED_RECHECK_MS 再呼叫一次。
 * holdExpire：付款送出中先不查（扣款結果自己會處理），送完還是過期再查。
 */
export const usePaymentLockCountdown = (
  expiresAt: string | null | undefined,
  active: boolean,
  onExpire: () => void,
  holdExpire = false,
): number | null => {
  const expiresMs = expiresAt ? Date.parse(expiresAt) : NaN;
  const valid = Number.isFinite(expiresMs);
  const [now, setNow] = useState(() => Date.now());
  const onExpireRef = useRef(onExpire);
  onExpireRef.current = onExpire;

  useEffect(() => {
    if (!active || !valid) return undefined;
    setNow(Date.now());
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [active, valid]);

  const remaining = active && valid ? Math.max(0, expiresMs - now) : null;
  const expired = remaining === 0;

  useEffect(() => {
    if (!expired || holdExpire) return undefined;
    onExpireRef.current();
    const id = setInterval(() => onExpireRef.current(), LOCK_EXPIRED_RECHECK_MS);
    return () => clearInterval(id);
  }, [expired, holdExpire]);

  return remaining;
};

/** 付款按鈕旁的倒數提示 */
export const PaymentLockCountdownNote = ({ remainingMs }: { remainingMs: number }) => (
  <XStack ai="center" jc="center" gap="$1.5" data-testid="payment-lock-countdown">
    <RiTimeLine size={14} color={colors.amberDark} />
    <Text fontSize={12} color={colors.amberDark} ta="center" style={{ fontVariantNumeric: "tabular-nums" }}>
      {remainingMs > 0 ? `請在 ${formatLockRemaining(remainingMs)} 內完成付款，逾時需請配送員重新請款` : "付款時間已到，確認付款狀態中…"}
    </Text>
  </XStack>
);
